import { getEffectiveParam } from "@/features/config"
import {
  SUBSCRIPTION_PLAN_IDS_WITH_INCLUDED_ASTRO_MAP_REPORT,
  SUBSCRIPTION_PLAN_IDS_WITH_INCLUDED_AURA_SCAN_REPORT,
  SUBSCRIPTION_PLAN_IDS_WITH_INCLUDED_SOULMATE_REPORT,
} from "@/features/subscriptions/report-plan-codes"
import { usePurchaseStore } from "@/stores/purchase-store"
import type { OfferEntry, OfferKey } from "../types"
import { OFFER_CONFIG_PARAM, OFFER_ORDER, OFFER_PRESETS } from "./offer-registry"

/** Offers already granted by the just-purchased main plan — skip their upsell. */
function isOfferIncludedInPurchasedPlan(key: OfferKey, planId: string | null): boolean {
  if (!planId) return false
  if (key === "soulmate") {
    return SUBSCRIPTION_PLAN_IDS_WITH_INCLUDED_SOULMATE_REPORT.includes(planId)
  }
  if (key === "astromap") {
    return SUBSCRIPTION_PLAN_IDS_WITH_INCLUDED_ASTRO_MAP_REPORT.includes(planId)
  }
  if (key === "aura") {
    return SUBSCRIPTION_PLAN_IDS_WITH_INCLUDED_AURA_SCAN_REPORT.includes(planId)
  }
  return false
}

/**
 * The ordered list of additional offers to show. An offer is included when its
 * `<offer>_report` config param holds a plan id AND a frontend preset exists
 * for it. Order follows {@link OFFER_ORDER}, not the param order, so the funnel
 * is deterministic regardless of how config is authored. Supports 2–5 offers
 * (or none) purely by which params are present.
 *
 * Soulmate / Astro Map / Aura Scan offers are omitted when the main checkout
 * plan already includes that report.
 */
export function resolveOfferEntries(): OfferEntry[] {
  const purchasedPlanId = usePurchaseStore.getState().planId
  const entries: OfferEntry[] = []
  for (const key of OFFER_ORDER) {
    if (!OFFER_PRESETS[key]) continue
    if (isOfferIncludedInPurchasedPlan(key, purchasedPlanId)) continue
    const planId = getEffectiveParam(OFFER_CONFIG_PARAM[key])?.trim()
    if (planId) entries.push({ key, planId })
  }
  return entries
}

/** Whether checkout should route into the additional-offers funnel. */
export function hasAdditionalOffers(): boolean {
  return resolveOfferEntries().length > 0
}
